import { appKey } from '@app/shared/brand'
import { LayoutGrid, List, type LucideIcon } from 'lucide-react'
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  type Ref,
  useState,
} from 'react'
import { SegmentedControl } from './SegmentedControl'
import './nav-list.css'

/** How a navigation list is shown: a row each, or each as its picture. */
export type NavListView = 'list' | 'thumbnails'

/**
 * A list's own choice of view, kept per browser under the list's name. Each
 * list remembers its own: a deck's slides read as pictures while a book's
 * contents reads as names, so one choice for all would be wrong in one of them.
 */
export function useNavListView(
  name: string,
  initial: NavListView = 'list',
): [NavListView, (view: NavListView) => void] {
  const key = appKey(name)
  const [view, setView] = useState<NavListView>(() => {
    try {
      const stored = localStorage.getItem(key)
      return stored === 'list' || stored === 'thumbnails' ? stored : initial
    } catch {
      return initial
    }
  })
  const choose = (next: NavListView) => {
    setView(next)
    try {
      localStorage.setItem(key, next)
    } catch {
      /* Held for this visit only. */
    }
  }
  return [view, choose]
}

/**
 * A list in the left bar that navigates documents or their parts, shown as
 * names or as pictures by the reader's choice. The switch sits at the list's
 * trailing edge; `before` holds what the list says about itself, above it.
 */
export function NavList({
  label,
  view,
  onViewChange,
  ordered = false,
  listRef,
  before,
  children,
}: {
  /** What the list holds, for a screen reader: "Slides", "Open documents". */
  label: string
  view: NavListView
  onViewChange: (view: NavListView) => void
  /** Numbered in order, as a deck's slides are. */
  ordered?: boolean
  listRef?: Ref<HTMLOListElement & HTMLUListElement>
  before?: ReactNode
  children: ReactNode
}) {
  const ListTag = ordered ? 'ol' : 'ul'
  return (
    <div className="nav-list" data-view={view}>
      <div className="nav-list__head">
        {before}
        <SegmentedControl
          size="sm"
          ariaLabel={`Show ${label.toLowerCase()} as`}
          value={view}
          onChange={onViewChange}
          options={[
            { value: 'list', icon: List, ariaLabel: 'List' },
            { value: 'thumbnails', icon: LayoutGrid, ariaLabel: 'Thumbnails' },
          ]}
        />
      </div>
      <ListTag className="nav-list__list" aria-label={label} ref={listRef}>
        {children}
      </ListTag>
    </div>
  )
}

type EntryProps = {
  className: string
  'aria-current'?: 'page' | 'true'
  children: ReactNode
}

/**
 * One entry: its picture above its name as a thumbnail, its kind and name as a
 * row. The entry itself is whatever navigates — a link to a document, a button
 * to a slide — supplied through `render`, and it carries the current mark.
 */
export function NavListItem({
  view,
  current = false,
  currentAs = 'page',
  icon: Icon,
  number,
  name,
  picture,
  trailing,
  render,
  ...item
}: {
  view: NavListView
  current?: boolean
  /** `page` for a document; `true` for a place within the one on screen. */
  currentAs?: 'page' | 'true'
  icon?: LucideIcon
  number?: number
  name: ReactNode
  /** Drawn only as a thumbnail. */
  picture?: ReactNode
  /** A control beside the entry, never inside it: a document's close. */
  trailing?: ReactNode
  render: (props: EntryProps) => ReactNode
} & Omit<ComponentPropsWithoutRef<'li'>, 'children'>) {
  return (
    <li
      {...item}
      className="nav-list__item"
      {...(current ? { 'data-current': '' } : {})}
    >
      {render({
        className: 'nav-list__entry',
        'aria-current': current ? currentAs : undefined,
        children: (
          <>
            {view === 'thumbnails' && picture ? (
              <span className="nav-list__picture">{picture}</span>
            ) : null}
            <span className="nav-list__name">
              {number !== undefined ? (
                <span className="nav-list__number">{number}</span>
              ) : null}
              {Icon ? <Icon size={16} aria-hidden /> : null}
              <span className="nav-list__title" dir="auto">
                {name}
              </span>
            </span>
          </>
        ),
      })}
      {trailing}
    </li>
  )
}
